import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, PackageCheck, Printer, Truck, Wrench } from 'lucide-react';
import { apiGet, fmt } from '../lib/api';

const TRACK_STEPS = [
  { icon: Check, label: 'Order confirmed' },
  { icon: PackageCheck, label: 'Picked & packed' },
  { icon: Truck, label: 'In transit' },
  { icon: Wrench, label: 'Delivered' },
];

const STAGES = { confirmed: 1, picked: 2, packed: 2, shipped: 3, in_transit: 3, delivered: 4 };

export default function OrderConfirmation() {
  const { number } = useParams();
  const [order, setOrder] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    apiGet(`/orders/${number}`).then(setOrder).catch(() => setMissing(true));
  }, [number]);

  if (missing) {
    return (
      <div className="bg-cream min-h-screen grid place-items-center px-4 py-32 text-center">
        <div>
          <h1 className="font-display font-extrabold uppercase text-forest text-4xl">Order not found</h1>
          <p className="text-sub mt-2 mb-8">We couldn't find order {number}.</p>
          <Link to="/shop" className="inline-block bg-flame text-white font-display font-semibold uppercase px-8 py-4 rounded-full">Back to Shop</Link>
        </div>
      </div>
    );
  }

  if (!order) {
    return <div className="bg-cream min-h-screen pt-40"><div className="max-w-3xl mx-auto px-4 animate-pulse space-y-4"><div className="h-12 bg-silver rounded" /><div className="h-40 bg-silver rounded-2xl" /></div></div>;
  }

  const stage = STAGES[order.status] || 1;

  return (
    <div className="bg-cream min-h-screen">
      <div className="bg-forest noise relative">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: 'spring', damping: 12 }} className="mx-auto w-16 h-16 rounded-full bg-flame grid place-items-center mb-6">
            <Check className="w-8 h-8 text-white" />
          </motion.div>
          <p className="font-mono text-[11px] uppercase tracking-widest2 text-flame mb-2">Thank you, {order.customer.name.split(' ')[0]}</p>
          <h1 className="font-display font-extrabold uppercase tracking-tight text-cream text-4xl sm:text-5xl leading-none">
            Your parts are<br />on the board.
          </h1>
          <p className="text-cream/60 mt-4 text-sm">
            Order <span data-testid="order-number" className="font-mono font-semibold text-cream">{order.order_number}</span> · confirmation sent to {order.customer.email}
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="bg-white border border-emerald-950/10 rounded-3xl p-7 mb-8">
          <div className="flex items-center justify-between mb-7">
            <h2 className="font-display font-bold uppercase text-forest text-2xl">Shipment Tracking</h2>
            <button data-testid="print-invoice-button" onClick={() => window.print()} className="inline-flex items-center gap-2 text-sm font-semibold text-forest border border-emerald-950/15 rounded-full px-4 py-2 hover:border-flame hover:text-flame transition">
              <Printer className="w-4 h-4" /> Print Invoice
            </button>
          </div>
          <div className="flex justify-between relative">
            <div className="absolute left-5 right-5 top-5 h-0.5 bg-silver">
              <motion.div initial={{ width: 0 }} animate={{ width: `${(stage / 3) * 100}%` }} transition={{ duration: 1.2, delay: 0.4 }} className="h-full bg-flame" />
            </div>
            {TRACK_STEPS.map((s, i) => (
              <div key={s.label} className="relative flex flex-col items-center gap-2 w-1/4">
                <span className={`grid place-items-center w-10 h-10 rounded-full border-2 ${i < stage ? 'bg-flame border-flame text-white' : 'bg-white border-silver text-sub'}`}>
                  <s.icon className="w-4 h-4" />
                </span>
                <span className={`font-mono text-[9px] uppercase tracking-wide text-center ${i < stage ? 'text-forest font-semibold' : 'text-sub'}`}>{s.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          <div className="bg-white border border-emerald-950/10 rounded-3xl p-7">
            <h2 className="font-display font-bold uppercase text-forest text-xl mb-4">Items ({order.items.length})</h2>
            <div className="space-y-3">
              {order.items.map((i) => (
                <div key={i.product_id} className="flex gap-3 items-center">
                  <img src={i.image} alt={i.name} className="w-12 h-12 rounded-lg object-cover bg-forest shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-forest truncate">{i.name}</p>
                    <p className="font-mono text-[10px] text-sub uppercase tracking-widest">×{i.qty} · {i.sku}</p>
                  </div>
                  <span className="text-sm font-semibold text-forest">{fmt(i.price * i.qty)}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-emerald-950/10 mt-4 pt-4 space-y-1.5 text-sm">
              <div className="flex justify-between text-sub"><span>Subtotal</span><span>{fmt(order.subtotal)}</span></div>
              <div className="flex justify-between text-sub"><span>Shipping</span><span>{order.shipping === 0 ? 'FREE' : fmt(order.shipping)}</span></div>
              <div className="flex justify-between text-sub"><span>Tax</span><span>{fmt(order.tax)}</span></div>
              <div className="flex justify-between items-end pt-2">
                <span className="font-semibold text-forest">Total paid</span>
                <span data-testid="order-total" className="font-display font-bold text-2xl text-forest">{fmt(order.total)}</span>
              </div>
            </div>
          </div>

          <div className="bg-white border border-emerald-950/10 rounded-3xl p-7">
            <h2 className="font-display font-bold uppercase text-forest text-xl mb-4">Delivering to</h2>
            <p className="text-sm text-ink leading-relaxed">
              {order.customer.name}<br />
              {order.customer.address}<br />
              {order.customer.city}, {order.customer.state} {order.customer.zip}
            </p>
            <p className="font-mono text-[11px] uppercase tracking-widest text-sub mt-4">
              Method: {order.shipping_method === 'standard' ? 'Standard Freight (3–5 days)' : order.shipping_method === 'expedited' ? 'Expedited Air (next day)' : 'Lift-Gate Truck'}
            </p>
            <Link to="/shop" data-testid="order-continue-shopping" className="mt-7 w-full text-center inline-block bg-forest hover:bg-flame text-cream font-display font-semibold uppercase tracking-wide px-6 py-3.5 rounded-full transition-colors">
              Keep Shopping
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
